import React, { useState, useEffect } from 'react';
import { personalNotes } from '../api';
import RichTextEditor from './RichTextEditor';
import FormattedContent from './FormattedContent';
import { getPlainText } from '../utils/htmlText';
import './CoachCommentThread.css';

/**
 * CoachCommentThread - Display comments on a personal note (coach view)
 */
const CoachCommentThread = ({ playerId, noteId, comments = [], onCommentAdded, currentCoachId }) => {
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [localComments, setLocalComments] = useState(comments || []);

  useEffect(() => {
    setLocalComments(comments || []);
  }, [comments]);

  const handleAddComment = async (e) => {
    e.preventDefault();
    setError(null);

    const plainText = getPlainText(newComment);
    if (!plainText) {
      setError('Comment cannot be empty');
      return;
    }
    if (plainText.length > 1000) {
      setError('Comment cannot exceed 1000 characters');
      return;
    }

    setSubmitting(true);
    try {
      const response = await personalNotes.addComment(playerId, noteId, newComment);

      // Add to local comments
      setLocalComments([...localComments, response.data.comment]);
      setNewComment('');

      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (err) {
      console.error('❌ Error adding comment:', err);
      setError(err.response?.data?.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) {
      return;
    }

    try {
      await personalNotes.deleteComment(playerId, noteId, commentId);
      setLocalComments(localComments.filter(c => c.commentId !== commentId));

      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (err) {
      console.error('❌ Error deleting comment:', err);
      setError(err.response?.data?.message || 'Failed to delete comment');
    }
  };

  const getTimeAgo = (date) => {
    if (!date) return 'Just now';

    const now = new Date();
    const commentDate = new Date(date);
    const diffMs = now - commentDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return commentDate.toLocaleDateString();
  };

  return (
    <div className="coach-comment-thread">
      <h4>💬 Coach Comments</h4>

      {error && (
        <div className="comment-error">
          ⚠️ {error}
        </div>
      )}

      {/* Comments List */}
      {localComments.length === 0 ? (
        <div className="no-comments">
          <p>No comments yet. Be the first to comment!</p>
        </div>
      ) : (
        <div className="comments-list">
          {localComments.map(comment => (
            <div key={comment.commentId} className="comment-item">
              <div className="comment-header">
                <p className="comment-author">{comment.coachName}</p>
                <span className="comment-time">
                  {getTimeAgo(comment.createdAt)}
                </span>
              </div>
              <div className="comment-text"><FormattedContent content={comment.text} /></div>
              {comment.coachId === currentCoachId && (
                <button
                  className="btn-delete-comment"
                  onClick={() => handleDeleteComment(comment.commentId)}
                  title="Delete comment"
                >
                  🗑️ Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Comment Form */}
      <form className="comment-form" onSubmit={handleAddComment}>
        <RichTextEditor
          value={newComment}
          onChange={setNewComment}
          maxCharacters={1000}
          placeholder="Add a comment..."
          disabled={submitting}
        />
        <div className="comment-form-footer">
          <button
            type="submit"
            className="btn-submit-comment"
            disabled={submitting || !getPlainText(newComment)}
          >
            {submitting ? 'Adding...' : 'Add Comment'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CoachCommentThread;
